import { Badge, Box, Grid, Heading, HStack, Text } from "@chakra-ui/react";
import { quarterStart } from "./placingBook";
import { isInterested } from "./placingUtils";
import { isNewSince } from "../../hooks/useSeenStages";

export const STAGE_BLOCKS = [
  { key: "build", title: "Build" },
  { key: "quotation", title: "Quotation" },
  { key: "order", title: "Order" },
  { key: "signing", title: "Signing" },
];
const raw = (v) => v?.$date ?? v;

/**
 * The broker's four blocks from their placements' summaries: how many are
 * at each stage, how many need them, and how many are new since the stage
 * was last opened (seenAt from useSeenStages).
 */
export const brokerStageBlocks = (summaries, seenAt) =>
  STAGE_BLOCKS.map(({ key, title }) => {
    const here = summaries.filter((s) => s.stageKey === key);
    const sum = (f) => here.reduce((t, s) => t + f(s), 0);
    const detail = {
      build: `${sum((s) => s.leads.filter(isInterested).length)} interested · ${sum((s) => s.leads.filter((l) => l.status === "declined").length)} declined`,
      quotation: `${sum((s) => s.quotes.length)} quotes in · ${sum((s) => s.quoteRequests.filter((q) => q.status === "sent").length)} awaited`,
      order: `${sum((s) => new Set(s.offers.map((o) => o.sender?.partyId)).size)} lines written · ${sum((s) => s.lineRequests.filter((r) => r.status === "sent").length)} awaited`,
      signing: `${here.filter((s) => new Date(raw(s.placement.signedDownDate) || 0) >= quarterStart()).length} signed down this quarter`,
    }[key];
    return {
      key,
      title,
      count: here.length,
      unit: here.length === 1 ? "placement" : "placements",
      needs: here.filter((s) => s.needsYou).length,
      newCount: here.filter((s) => isNewSince(seenAt, key, s.carrierUpdated)).length,
      detail,
    };
  });

/**
 * The four process blocks on a placing portal page (Build, Quotation,
 * Order, Signing). Clicking one filters the list to that stage; clicking it
 * again shows everything. blocks: [{ key, title, count, unit, detail,
 * needs, newCount }].
 */
const PortalStageBlocks = ({ blocks, selected, onSelect }) => (
  <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }} gap={3}>
    {blocks.map((b) => {
      const isSelected = selected === b.key;
      return (
        <Box
          key={b.key}
          as="button"
          type="button"
          textAlign="left"
          aria-pressed={isSelected}
          onClick={() => onSelect(isSelected ? null : b.key)}
          borderWidth={isSelected ? "2px" : "1px"}
          borderColor={isSelected ? "blue.600" : "gray.200"}
          bg={isSelected ? "white" : "whiteAlpha.800"}
          boxShadow={isSelected ? "md" : undefined}
          borderRadius="lg"
          p={4}
          _hover={{ borderColor: isSelected ? "blue.600" : "blue.300" }}
          transition="border-color 0.15s">
          <HStack justify="space-between" mb={1}>
            <Heading size="sm">{b.title}</Heading>
            {b.newCount > 0 && (
              <Badge colorScheme="red" variant="solid" borderRadius="full" px={2}>{b.newCount} new</Badge>
            )}
          </HStack>
          <HStack align="baseline" spacing={2}>
            <Text fontSize="2xl" fontWeight="semibold" fontFamily="mono" lineHeight="1.2">{b.count}</Text>
            <Text fontSize="sm" color="gray.600">{b.unit}</Text>
            {b.needs > 0 && (
              <Badge colorScheme="orange" borderRadius="full" px={2} textTransform="none">{b.needs} need{b.needs === 1 ? "s" : ""} you</Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>{b.detail}</Text>
        </Box>
      );
    })}
  </Grid>
);

export default PortalStageBlocks;
